import { Dispatch, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import {
  Application_Type,
  Criteria_Config_Set_Input,
  Criteria_Group_Config,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { CRITERIA_CONFIG_PROCESSES } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import {
  queryCriteriaConfigById,
  updateCriteriaConfigById,
} from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import {
  CriteriaConfigurationEvaluationCriteriaForm,
  EvaluationCriteriaFormValues,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/edit/CriteriaConfigurationEvaluationCriteriaForm'
import {
  canUpdateEvaluationCriteriaStatus,
  fetchCriteriaGroupsByProcesses,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/edit/EvaluationCriteriaFormUtils'
import { fetchApplicationTypesByProcesses } from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/EvaluationCriteriaGridUtils'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'
import { resolveCriteriaConfigRoot } from 'src/routing/routing-utils'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

const submitCriteriaConfigForm = () => {
  document.getElementById('evaluation-criteria-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const CriteriaConfigurationEvaluationCriteriaEditPage = () => {
  const { criteriaId } = useParams()
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<EvaluationCriteriaFormValues | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const [criteriaGroups, setCriteriaGroups] = useState<Criteria_Group_Config[]>([])
  const [applicationTypes, setApplicationTypes] = useState<Application_Type[]>([])

  const fetchCriteriaGroups = useCallback(async () => {
    const { fetchedCriteriaGroups } = await fetchCriteriaGroupsByProcesses(urqlClient)(CRITERIA_CONFIG_PROCESSES)

    return fetchedCriteriaGroups
  }, [urqlClient])

  const fetchApplicationTypes = useCallback(async () => {
    const { fetchedApplicationTypes } = await fetchApplicationTypesByProcesses(urqlClient)(CRITERIA_CONFIG_PROCESSES)

    return fetchedApplicationTypes
  }, [urqlClient])

  const onBackHandler = useCallback(() => {
    const processTypeFromState = state?.processType

    if (processTypeFromState !== null && processTypeFromState !== undefined) {
      navigate(resolveCriteriaConfigRoot(processTypeFromState))
    } else {
      navigate(ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationEvaluationCriteria.params({}))
    }
  }, [navigate, state])

  const onSave = useCallback(() => {
    originRef.current = 'SAVE'
    submitCriteriaConfigForm()
  }, [])

  const onSaveAndBack = useCallback(() => {
    originRef.current = 'SAVE_AND_BACK'
    submitCriteriaConfigForm()
  }, [])

  const fetchEvaluationCriteriaToEdit = useCallback(
    async (applicationTypes: Application_Type[]) => {
      if (!criteriaId) {
        throw new Error()
      }

      const { data, error } = await urqlClient
        .query<{ criteria_config: Query_Root['criteria_config'] }>(queryCriteriaConfigById, { criteriaId })
        .toPromise()

      if (error || !data?.criteria_config?.[0]) {
        throw new Error('Resource not found')
      }

      const fetchedCriteriaConfig = data.criteria_config[0]
      const fetchedCriteriaConfigProcess = fetchedCriteriaConfig.processes[0]
      if (!fetchedCriteriaConfigProcess || !CRITERIA_CONFIG_PROCESSES.includes(fetchedCriteriaConfigProcess)) {
        throw new Error('Invalid state of fetched criteria config processes')
      }

      const pfKapApplicationTypes = applicationTypes.filter((item) => item.process === PROCESS.PF_KAP)
      const pfPgvApplicationTypes = applicationTypes.filter((item) => item.process === PROCESS.PF_PGV)

      const editingCriteriaConfig: EvaluationCriteriaFormValues = {
        criteriaType: fetchedCriteriaConfig.criteria_type,
        criteriaId: fetchedCriteriaConfig.criteria_id,
        status: fetchedCriteriaConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
        deDescription: fetchedCriteriaConfig.description['de'],
        enDescription: fetchedCriteriaConfig.description['en'],
        frDescription: fetchedCriteriaConfig.description['fr'],
        itDescription: fetchedCriteriaConfig.description['it'],
        processAlloc: fetchedCriteriaConfigProcess,
        pfKapCriteriaGroup:
          fetchedCriteriaConfigProcess === PROCESS.PF_KAP ? fetchedCriteriaConfig.criteria_group_id ?? null : null,
        pfPgvCriteriaGroup:
          fetchedCriteriaConfigProcess === PROCESS.PF_PGV ? fetchedCriteriaConfig.criteria_group_id ?? null : null,
        factsheetCriteriaGroup:
          fetchedCriteriaConfigProcess === PROCESS.FACTSHEET ? fetchedCriteriaConfig.criteria_group_id ?? null : null,
        pfKapApplicationTypes:
          fetchedCriteriaConfigProcess === PROCESS.PF_KAP
            ? fetchedCriteriaConfig.application_type_ids.filter((aTypeId: number) =>
                pfKapApplicationTypes.some((item) => item.id === aTypeId),
              )
            : [],
        pfPgvApplicationTypes:
          fetchedCriteriaConfigProcess === PROCESS.PF_PGV
            ? fetchedCriteriaConfig.application_type_ids.filter((aTypeId: number) =>
                pfPgvApplicationTypes.some((item) => item.id === aTypeId),
              )
            : [],
        factsheetLevels: fetchedCriteriaConfig.levels,
      }

      return { editingCriteriaConfig }
    },
    [criteriaId, urqlClient],
  )

  const handleSubmitLocal = useCallback(
    async (values: EvaluationCriteriaFormValues, setFormDirty: Dispatch<boolean>) => {
      if (!canUpdateEvaluationCriteriaStatus(values, criteriaGroups)) {
        notificationService.error(getMessage('notification.edit.evaluation.criteria.status.invalid'))
        return
      }

      const criteriaConfigFieldsSetClause: Criteria_Config_Set_Input = {
        criteria_id: values.criteriaId,
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        description: {
          de: values.deDescription ? HtmlSanitizer.sanitize(values.deDescription) : '',
          en: values.enDescription ? HtmlSanitizer.sanitize(values.enDescription) : '',
          fr: values.enDescription ? HtmlSanitizer.sanitize(values.frDescription) : '',
          it: values.itDescription ? HtmlSanitizer.sanitize(values.itDescription) : '',
        },
      }

      const criteriaIdParsed = parseInt(criteriaId as string)
      const { data, error } = await urqlClient
        .mutation<{
          update_criteria_config: Mutation_Root['update_criteria_config']
        }>(updateCriteriaConfigById, {
          criteriaId: criteriaIdParsed,
          criteriaConfigFieldsSetClause: criteriaConfigFieldsSetClause,
        })
        .toPromise()
      if (!error && data?.update_criteria_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        setFormDirty(false)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [criteriaGroups, criteriaId, urqlClient, notificationService, getMessage, onBackHandler],
  )

  useEffect(() => {
    Promise.all([fetchCriteriaGroups(), fetchApplicationTypes()])
      .then(([criteriaGroups, applicationTypes]) => {
        setCriteriaGroups(criteriaGroups)
        setApplicationTypes(applicationTypes)

        return fetchEvaluationCriteriaToEdit(applicationTypes)
      })
      .then((res) => {
        setInitialValues(res.editingCriteriaConfig)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchApplicationTypes, fetchCriteriaGroups, fetchEvaluationCriteriaToEdit, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.evaluation.criteria.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <CriteriaConfigurationEvaluationCriteriaForm
              mode="EDIT"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              criteriaGroups={criteriaGroups}
              applicationTypes={applicationTypes}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
